// Payroll service - comprehensive payroll management
import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    setDoc,
    query,
    where,
    serverTimestamp,
    writeBatch
} from 'firebase/firestore';
import { db } from '../../firebase';

// Import related services
import { journalEntriesService } from './journal-entries';
import { accountMappingService } from './account-mapping';
import { driversService } from '../logistics/drivers';
import { shipmentsService } from '../logistics/shipments';
import { shipmentCostingsService } from '../logistics/shipment-costings';
import { employeesService } from './employees';

export const payrollService = {
    /**
     * Generate payroll period ID
     */
    generatePayrollPeriodId(year, month) {
        return `${year}-${String(month).padStart(2, '0')}`;
    },

    /**
     * Get payroll periods for a company
     */
    async getPayrollPeriods(companyId) {
        try {
            const periodsRef = collection(db, 'payrollPeriods');
            const q = query(
                periodsRef,
                where('companyId', '==', companyId)
            );
            const snapshot = await getDocs(q);
            
            const periods = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Sort by year and month (most recent first)
            return periods.sort((a, b) => {
                if (a.year !== b.year) return b.year - a.year;
                return b.month - a.month;
            });
        } catch (error) {
            console.error('Error getting payroll periods:', error);
            throw error;
        }
    },

    /**
     * Get payroll entries for a period
     */
    async getPayrollEntries(companyId, periodId) {
        try {
            const entriesRef = collection(db, 'payrollEntries');
            const q = query(
                entriesRef,
                where('companyId', '==', companyId),
                where('periodId', '==', periodId)
            );
            const snapshot = await getDocs(q);
            
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error('Error getting payroll entries:', error);
            throw error;
        }
    },

    /**
     * Update payroll period status
     */
    async updatePayrollPeriodStatus(periodId, newStatus, updatedBy) {
        try {
            const periodRef = doc(db, 'payrollPeriods', periodId);
            await updateDoc(periodRef, {
                status: newStatus,
                updatedBy,
                updatedAt: serverTimestamp()
            });
            return true;
        } catch (error) {
            console.error('Error updating payroll period status:', error);
            throw error;
        }
    },

    /**
     * Create payroll period
     */
    async createPayrollPeriod(companyId, year, month, createdBy) {
        try {
            console.log('🔄 Creating payroll period:', { companyId, year, month, createdBy });

            // Check if period already exists
            const periodId = this.generatePayrollPeriodId(year, month);
            const existingPeriodRef = doc(db, 'payrollPeriods', periodId);
            const existingPeriod = await getDoc(existingPeriodRef);
            
            if (existingPeriod.exists()) {
                throw new Error(`Payroll period for ${year}-${String(month).padStart(2, '0')} already exists`);
            }

            // Calculate period dates
            const startDate = new Date(year, month - 1, 1);
            const endDate = new Date(year, month, 0);

            // Get employees and calculate totals
            const employees = await this.getEmployees(companyId);
            const activeEmployees = employees.filter(emp => emp.status === 'active');

            console.log('👥 Active employees found:', activeEmployees.length);
            console.log('💰 Employee salaries:', activeEmployees.map(emp => ({ 
                name: emp.name, 
                salary: emp.salary, 
                type: emp.type,
                commissionEligible: emp.commissionEligible 
            })));

            // Calculate totals for the period
            let totalSalaries = 0;
            let totalCommissions = 0;

            for (const employee of activeEmployees) {
                // Add salary - ensure it's a valid number
                const salary = parseFloat(employee.salary) || 0;
                totalSalaries += salary;
                console.log(`💰 Employee ${employee.name}: Salary ${salary}, Running total: ${totalSalaries}`);

                // Calculate commissions for drivers
                if (employee.type === 'driver' && employee.commissionEligible) {
                    try {
                        console.log(`🔍 Calculating commissions for driver: ${employee.name}`);
                        const commissionData = await this.calculateDriverCommissions(
                            employee.id,
                            companyId,
                            startDate,
                            endDate
                        );
                        const commissionAmount = commissionData.unpaidCommissions || 0;
                        totalCommissions += commissionAmount;
                        console.log(`💰 Driver ${employee.name}: Commission ${commissionAmount}, Running total: ${totalCommissions}`);
                    } catch (error) {
                        console.warn('Could not calculate commissions for employee:', employee.id, error);
                    }
                }
            }

            const totalPayable = totalSalaries + totalCommissions;

            console.log('📊 Final totals:', {
                activeEmployees: activeEmployees.length,
                totalSalaries,
                totalCommissions,
                totalPayable
            });

            // Create period document
            const periodData = {
                id: periodId,
                companyId,
                year,
                month,
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                status: 'draft',
                employeeCount: activeEmployees.length,
                totalSalaries,
                totalCommissions,
                totalPayable,
                createdBy,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            await setDoc(existingPeriodRef, periodData);
            console.log('✅ Payroll period created successfully:', periodId);

            return {
                id: periodId,
                ...periodData
            };
        } catch (error) {
            console.error('❌ Error creating payroll period:', error);
            throw error;
        }
    },

    /**
     * Delete payroll period
     */
    async deletePayrollPeriod(periodId) {
        try {
            // Check if there are any payroll entries for this period
            const entriesRef = collection(db, 'payrollEntries');
            const q = query(entriesRef, where('periodId', '==', periodId));
            const entriesSnapshot = await getDocs(q);
            
            if (!entriesSnapshot.empty) {
                throw new Error('Cannot delete period with existing payroll entries');
            }

            // Delete the period
            const periodRef = doc(db, 'payrollPeriods', periodId);
            await deleteDoc(periodRef);
            return true;
        } catch (error) {
            console.error('Error deleting payroll period:', error);
            throw error;
        }
    },

    /**
     * Generate payroll entry number
     */
    generatePayrollNumber() {
        const now = new Date();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `PAY-${year}${month}-${random}`;
    },

    /**
     * Get all employees (drivers + other employees)
     */
    async getEmployees(companyId) {
        try {
            console.log('🔍 Getting employees for company:', companyId);
            
            // Get drivers
            const drivers = await driversService.getDrivers(companyId);
            console.log('👥 Drivers found:', drivers.length);

            // Get other employees
            const employees = await employeesService.getEmployees(companyId);
            console.log('👥 Employees found:', employees.length);

            // Combine and standardize
            const allEmployees = [
                ...drivers.map(driver => {
                    console.log('🚛 Processing driver:', driver.name, 'Salary:', driver.salary);
                    return {
                        ...driver,
                        type: 'driver',
                        role: driver.role || 'Driver',
                        employeeId: driver.driverCode || driver.driverId || `DRV${String(driver.id).slice(-4)}`,
                        salary: parseFloat(driver.salary) || 0, // Ensure salary is a number
                        commissionRate: parseFloat(driver.commissionRate) || 0,
                        commissionEligible: Boolean(driver.commissionEligible),
                        status: driver.status || 'active'
                    };
                }),
                ...employees.map(emp => {
                    console.log('👤 Processing employee:', emp.name, 'Salary:', emp.salary);
                    return {
                        ...emp,
                        type: 'employee',
                        role: emp.role || 'Employee',
                        employeeId: emp.employeeId || `EMP${String(emp.employeeNumber || '0001').padStart(4, '0')}`,
                        salary: parseFloat(emp.salary) || 0, // Ensure salary is a number
                        commissionRate: parseFloat(emp.commissionRate) || 0,
                        commissionEligible: Boolean(emp.commissionEligible),
                        status: emp.status || 'active'
                    };
                })
            ];

            console.log('✅ Total employees processed:', allEmployees.length);
            console.log('💰 Employees with salary:', allEmployees.filter(e => e.salary > 0).length);
            
            return allEmployees; // Return all employees, let the UI filter them
        } catch (error) {
            console.error('❌ Error getting employees:', error);
            throw error;
        }
    },

    /**
     * Get employee by ID
     */
    async getEmployee(employeeId, type = 'employee') {
        try {
            if (type === 'driver') {
                return await driversService.getDriver(employeeId);
            } else {
                return await employeesService.getEmployee(employeeId);
            }
        } catch (error) {
            console.error('Error getting employee:', error);
            throw error;
        }
    },

    /**
     * Update employee salary and settings
     */
    async updateEmployeeSalary(employeeId, employeeData, updatedBy) {
        try {
            console.log('🔄 Updating employee salary:', { employeeId, employeeData, updatedBy });
            
            // First try to find the employee as a driver
            let employee = null;
            let isDriver = false;
            
            try {
                employee = await driversService.getDriver(employeeId);
                if (employee) {
                    isDriver = true;
                    console.log('✅ Found as driver:', employee.name);
                }
            } catch (driverError) {
                console.log('ℹ️ Not found as driver, checking employees...');
            }
            
            // If not found as driver, try as regular employee
            if (!employee) {
                try {
                    employee = await employeesService.getEmployee(employeeId);
                    if (employee) {
                        console.log('✅ Found as employee:', employee.name);
                    }
                } catch (employeeError) {
                    console.log('❌ Not found as employee either');
                }
            }
            
            if (!employee) {
                throw new Error(`Employee with ID ${employeeId} not found`);
            }

            // Validate salary data
            const salaryData = {
                salary: parseFloat(employeeData.salary) || 0,
                commissionRate: parseFloat(employeeData.commissionRate) || 0,
                commissionEligible: Boolean(employeeData.commissionEligible),
                status: employeeData.status || 'active'
            };
            
            console.log('💾 Saving salary data:', salaryData);

            if (isDriver) {
                // Update driver record
                const driverRef = doc(db, 'drivers', employeeId);
                await updateDoc(driverRef, {
                    ...salaryData,
                    updatedBy,
                    updatedAt: serverTimestamp()
                });
                console.log('✅ Driver salary updated successfully');
            } else {
                // Update employee record using employees service
                await employeesService.updateEmployeeSalary(employeeId, salaryData, updatedBy);
                console.log('✅ Employee salary updated successfully');
            }

            // Log the salary change for audit trail
            try {
                const auditLog = {
                    employeeId,
                    employeeName: employee.name,
                    changeType: 'salary_update',
                    changes: salaryData,
                    updatedBy,
                    createdAt: serverTimestamp()
                };

                const logsRef = collection(db, 'employeeAuditLogs');
                await addDoc(logsRef, auditLog);
                console.log('✅ Audit log created');
            } catch (auditError) {
                console.warn('⚠️ Failed to create audit log:', auditError);
                // Don't fail the whole operation if audit logging fails
            }

            return true;
        } catch (error) {
            console.error('❌ Error updating employee salary:', error);
            console.error('Error details:', error.message);
            throw new Error(`Failed to update employee salary: ${error.message}`);
        }
    },

    /**
     * Calculate driver commissions for payroll processing
     */
    async calculateDriverCommissions(driverId, companyId, startDate, endDate) {
        try {
            console.log('🔍 Calculating driver commissions for payroll:', { driverId, companyId, startDate, endDate });
            
            const commissions = await this.getEmployeeCommissions(driverId, companyId, startDate, endDate);
            
            const totalCommission = commissions.reduce((sum, commission) => sum + (commission.amount || 0), 0);
            const paidCommissions = commissions.filter(c => c.isPaid).reduce((sum, c) => sum + (c.amount || 0), 0);
            const unpaidCommissions = totalCommission - paidCommissions;
            
            console.log('💰 Commission calculation result:', {
                totalCommissions: commissions.length,
                totalCommission,
                paidCommissions,
                unpaidCommissions
            });
            
            return {
                totalCommission,
                paidCommissions,
                unpaidCommissions,
                commissionCount: commissions.length,
                commissions
            };
        } catch (error) {
            console.error('❌ Error calculating driver commissions:', error);
            console.error('Error details:', error.message);
            // Return zero values instead of throwing to prevent breaking the payroll calculation
            return {
                totalCommission: 0,
                paidCommissions: 0,
                unpaidCommissions: 0,
                commissionCount: 0,
                commissions: []
            };
        }
    },

    /**
     * Get employee commissions with detailed breakdown
     */
    async getEmployeeCommissions(employeeId, companyId, startDate, endDate) {
        try {
            console.log('🔍 Getting commissions for employee:', employeeId, 'company:', companyId);
            console.log('📅 Date range:', startDate, 'to', endDate);

            // Debug: Let's also check what employee data we have
            const employee = await this.getEmployee(employeeId, 'driver');
            console.log('👤 Employee data:', employee);

            // First, let's try to get commissions from shipment costings
            const costingsRef = collection(db, 'shipmentCostings');

            // Try a broader query first to see what data we have
            const broadQuery = query(costingsRef, where('companyId', '==', companyId));
            const broadSnapshot = await getDocs(broadQuery);

            console.log('📊 Total costings found:', broadSnapshot.docs.length);

            const commissions = [];

            // Check all costings to see which ones have the driver assigned
            for (const doc of broadSnapshot.docs) {
                const costing = { id: doc.id, ...doc.data() };

                console.log('🔍 Checking costing:', costing.id, 'driverId:', costing.driverId, 'commission:', costing.commission);

                // Check if this costing is for our driver and has commission
                const commissionAmount = costing.commissionAmount || costing.commission || 0;
                if (costing.driverId === employeeId && commissionAmount > 0) {
                    console.log('✅ Found matching costing with commission:', costing.commission);

                    // Get the creation date for filtering
                    let costingDate;
                    if (costing.createdAt?.toDate) {
                        costingDate = costing.createdAt.toDate();
                    } else if (costing.createdAt) {
                        costingDate = new Date(costing.createdAt);
                    } else {
                        costingDate = new Date(); // Fallback to current date
                    }

                    console.log('📅 Costing date:', costingDate);

                    // Check if within date range
                    if (costingDate >= startDate && costingDate <= endDate) {
                        console.log('✅ Costing is within date range');

                        // Get shipment details
                        let shipment = null;
                        try {
                            shipment = await shipmentsService.getShipment(costing.shipmentId, companyId);
                            console.log('📦 Shipment found:', shipment?.trackingNumber, 'status:', shipment?.status);
                        } catch (shipmentError) {
                            console.warn('⚠️ Could not load shipment:', costing.shipmentId, shipmentError);
                        }

                        if (shipment) {
                            // Check if this commission has been paid
                            const commissionRecord = await this.getCommissionRecord(costing.shipmentId, employeeId);
                            console.log('💰 Commission payment record:', commissionRecord);

                            // Get client information
                            let clientInfo = null;
                            if (shipment.clientId) {
                                try {
                                    const { clientsService } = await import('../logistics/clients');
                                    clientInfo = await clientsService.getClient(shipment.clientId, companyId);
                                } catch (clientError) {
                                    console.warn('⚠️ Could not load client info:', shipment.clientId, clientError);
                                }
                            }

                            commissions.push({
                                id: `${costing.shipmentId}_${employeeId}`,
                                shipmentId: costing.shipmentId,
                                costingId: costing.id,
                                trackingNumber: shipment.trackingNumber || 'N/A',
                                tripNumber: shipment.tripNumber || costing.tripNumber || 'N/A',
                                origin: shipment.origin || 'N/A',
                                destination: shipment.destination || 'N/A',
                                deliveryDate: shipment.deliveryDate || costingDate.toISOString(),
                                costingDate: costingDate.toISOString(),
                                amount: commissionAmount,
                                isPaid: commissionRecord?.isPaid || false,
                                paidDate: commissionRecord?.paidDate || null,
                                paidBy: commissionRecord?.paidBy || null,
                                notes: costing.notes || '',
                                // Client information
                                clientId: shipment.clientId || null,
                                clientName: clientInfo?.name || clientInfo?.companyName || 'N/A'
                            });

                            console.log('✅ Added commission to list');
                        }
                    } else {
                        console.log('❌ Costing is outside date range');
                    }
                }
            }

            console.log('📋 Total commissions found:', commissions.length);

            // If no commissions found from costings, try alternative approach
            if (commissions.length === 0) {
                console.log('🔄 No commissions found from costings, trying alternative approach...');
                return await this.getEmployeeCommissionsFromShipments(employeeId, companyId, startDate, endDate);
            }

            return commissions.sort((a, b) => new Date(b.deliveryDate) - new Date(a.deliveryDate));
        } catch (error) {
            console.error('❌ Error getting employee commissions:', error);
            throw error;
        }
    },

    /**
     * Alternative method to get commissions from shipments (fallback)
     */
    async getEmployeeCommissionsFromShipments(employeeId, companyId, startDate, endDate) {
        try {
            console.log('🔄 Using fallback method: getting commissions from shipments');

            // Get all shipments for this driver
            const shipments = await shipmentsService.getShipments(companyId);
            const driverShipments = shipments.filter(shipment =>
                shipment.driverId === employeeId &&
                shipment.status === 'delivered'
            );

            console.log('📦 Found driver shipments:', driverShipments.length);

            const commissions = [];

            for (const shipment of driverShipments) {
                const shipmentDate = new Date(shipment.deliveryDate || shipment.createdAt);

                // Check if within date range
                if (shipmentDate >= startDate && shipmentDate <= endDate) {
                    // Get costing data for commission calculation
                    try {
                        const costing = await shipmentCostingsService.getCostingByShipment(shipment.id);
                        const commissionAmount = costing?.commission || 0;

                        console.log('💰 Shipment:', shipment.trackingNumber, 'Commission:', commissionAmount);

                        if (commissionAmount > 0) {
                            // Check if this commission has been paid
                            const commissionRecord = await this.getCommissionRecord(shipment.id, employeeId);

                            // Get client information
                            let clientInfo = null;
                            if (shipment.clientId) {
                                try {
                                    const { clientsService } = await import('../logistics/clients');
                                    clientInfo = await clientsService.getClient(shipment.clientId, companyId);
                                } catch (clientError) {
                                    console.warn('⚠️ Could not load client info:', shipment.clientId, clientError);
                                }
                            }

                            commissions.push({
                                id: `${shipment.id}_${employeeId}`,
                                shipmentId: shipment.id,
                                costingId: costing?.id || null,
                                trackingNumber: shipment.trackingNumber || 'N/A',
                                tripNumber: shipment.tripNumber || 'N/A',
                                origin: shipment.origin || 'N/A',
                                destination: shipment.destination || 'N/A',
                                deliveryDate: shipment.deliveryDate || shipmentDate.toISOString(),
                                costingDate: shipmentDate.toISOString(),
                                amount: commissionAmount,
                                isPaid: commissionRecord?.isPaid || false,
                                paidDate: commissionRecord?.paidDate || null,
                                paidBy: commissionRecord?.paidBy || null,
                                notes: costing?.notes || '',
                                // Client information
                                clientId: shipment.clientId || null,
                                clientName: clientInfo?.name || clientInfo?.companyName || 'N/A'
                            });
                        }
                    } catch (costingError) {
                        console.warn('⚠️ Could not load costing for shipment:', shipment.id, costingError);
                    }
                }
            }

            console.log('📋 Fallback method found commissions:', commissions.length);
            return commissions.sort((a, b) => new Date(b.deliveryDate) - new Date(a.deliveryDate));
        } catch (error) {
            console.error('❌ Error in fallback commission method:', error);
            return [];
        }
    },

    /**
     * Get commission payment record
     */
    async getCommissionRecord(shipmentId, employeeId) {
        try {
            const commissionsRef = collection(db, 'commissionPayments');
            const q = query(
                commissionsRef,
                where('shipmentId', '==', shipmentId),
                where('employeeId', '==', employeeId)
            );
            const snapshot = await getDocs(q);

            if (!snapshot.empty) {
                return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
            }
            return null;
        } catch (error) {
            console.error('Error getting commission record:', error);
            return null;
        }
    },

    /**
     * Update commission payment status
     */
    async updateCommissionPaymentStatus(commissionIds, markAsPaid, updatedBy) {
        try {
            console.log('🔄 Starting commission payment update:', { commissionIds, markAsPaid, updatedBy });

            const batch = writeBatch(db);
            const commissionsRef = collection(db, 'commissionPayments');

            for (const commissionId of commissionIds) {
                console.log('🔍 Processing commission ID:', commissionId);

                // Parse commission ID to get shipment and employee info
                const [shipmentId, employeeId] = commissionId.split('_');
                console.log('📋 Parsed IDs:', { shipmentId, employeeId });

                // Check if commission record exists
                const existingRecord = await this.getCommissionRecord(shipmentId, employeeId);
                console.log('📝 Existing record:', existingRecord ? 'Found' : 'Not found');

                if (markAsPaid) {
                    console.log('💰 Marking as paid');

                    // Try to create expense and journal entry, but don't fail if it doesn't work
                    try {
                        const commissionDetails = await this.getCommissionDetails(shipmentId, employeeId);
                        if (commissionDetails) {
                            await this.createCommissionExpenseAndJournal(commissionDetails, updatedBy);
                            console.log('✅ Expense and journal entry created successfully');
                        }
                    } catch (expenseError) {
                        console.warn('⚠️ Could not create expense/journal entry:', expenseError.message);
                        // Continue with payment record update even if expense creation fails
                    }

                    if (existingRecord) {
                        // Update existing record
                        const recordRef = doc(db, 'commissionPayments', existingRecord.id);
                        batch.update(recordRef, {
                            isPaid: true,
                            paidDate: new Date().toISOString(),
                            paidBy: updatedBy,
                            updatedAt: serverTimestamp()
                        });
                    } else {
                        // Create new record
                        const newRecordRef = doc(commissionsRef);
                        batch.set(newRecordRef, {
                            shipmentId,
                            employeeId,
                            isPaid: true,
                            paidDate: new Date().toISOString(),
                            paidBy: updatedBy,
                            createdAt: serverTimestamp(),
                            updatedAt: serverTimestamp()
                        });
                    }
                } else {
                    console.log('🔄 Marking as unpaid');

                    // Try to reverse expense and journal entry, but don't fail if it doesn't work
                    try {
                        const commissionDetails = await this.getCommissionDetails(shipmentId, employeeId);
                        if (commissionDetails) {
                            await this.reverseCommissionExpenseAndJournal(commissionDetails, updatedBy);
                            console.log('✅ Expense and journal entry reversed successfully');
                        }
                    } catch (reverseError) {
                        console.warn('⚠️ Could not reverse expense/journal entry:', reverseError.message);
                        // Continue with payment record update even if reversal fails
                    }

                    if (existingRecord) {
                        // Update existing record
                        const recordRef = doc(db, 'commissionPayments', existingRecord.id);
                        batch.update(recordRef, {
                            isPaid: false,
                            paidDate: null,
                            paidBy: null,
                            updatedAt: serverTimestamp()
                        });
                    }
                }
            }

            console.log('💾 Committing batch update...');
            await batch.commit();
            console.log('✅ Commission payment status updated successfully');
            return true;
        } catch (error) {
            console.error('❌ Error updating commission payment status:', error);
            console.error('Error details:', error.message);
            throw error;
        }
    },

    /**
     * Get commission details for expense/journal entry creation
     */
    async getCommissionDetails(shipmentId, employeeId) {
        try {
            // Get costing details first to infer companyId safely
            const costing = await shipmentCostingsService.getCostingByShipment(shipmentId);
            if (!costing) return null;

            const inferredCompanyId = costing.companyId;

            // Try to get shipment details using inferred companyId; fall back to minimal placeholder
            let shipment = null;
            try {
                shipment = await shipmentsService.getShipment(shipmentId, inferredCompanyId);
            } catch (_) {
                shipment = {
                    id: shipmentId,
                    companyId: inferredCompanyId,
                    trackingNumber: null,
                    origin: null,
                    destination: null
                };
            }

            // Get employee details
            const employee = await this.getEmployee(employeeId, 'driver');
            if (!employee) return null;

            return {
                shipmentId,
                employeeId,
                shipment,
                costing,
                employee,
                commissionAmount: costing.commission || 0
            };
        } catch (error) {
            console.error('Error getting commission details:', error);
            return null;
        }
    },

    /**
     * Create expense and journal entry for commission payment
     */
    async createCommissionExpenseAndJournal(commissionDetails, createdBy) {
        try {
            const { shipment, employee, commissionAmount, shipmentId } = commissionDetails;

            if (commissionAmount <= 0) return;

            // Import services dynamically to avoid circular dependencies
            const { expensesService } = await import('./expenses');
            const { journalEntriesService } = await import('./journal-entries');

            // Create expense record
            const expenseData = {
                companyId: shipment.companyId,
                category: 'commission',
                amount: commissionAmount,
                description: `Commission payment for ${employee.name} - Shipment ${shipment.trackingNumber || shipmentId.slice(-8)}`,
                date: new Date().toISOString().split('T')[0],
                vendor: employee.name,
                paymentMethod: 'cash',
                vatRate: 0,
                vatAmount: 0,
                totalAmount: commissionAmount,
                status: 'approved',
                tags: ['commission', 'payroll'],
                attachments: [],
                receiptAttached: false,
                notes: `${shipment.origin || 'N/A'} → ${shipment.destination || 'N/A'}`,
                relatedShipmentId: shipmentId,
                relatedEmployeeId: employee.id,
                createdBy: 'system', // Mark as system-generated
                createdAt: new Date(),
                updatedAt: new Date()
            };

            const expenseId = await expensesService.addExpense(expenseData);

            // Create journal entry
            const journalEntryData = {
                companyId: shipment.companyId,
                date: new Date().toISOString().split('T')[0],
                reference: `COMM-${shipmentId.slice(-8)}`,
                description: `Commission payment - ${employee.name} - ${shipment.trackingNumber || shipmentId.slice(-8)}`,
                type: 'commission_payment',
                entries: [
                    {
                        accountCode: '5700', // Commission Expense
                        accountName: 'Commission Expenses',
                        debit: commissionAmount,
                        credit: 0,
                        description: `Commission expense for ${employee.name}`
                    },
                    {
                        accountCode: '1000', // Cash
                        accountName: 'Cash',
                        debit: 0,
                        credit: commissionAmount,
                        description: `Commission payment to ${employee.name}`
                    }
                ],
                totalDebit: commissionAmount,
                totalCredit: commissionAmount,
                status: 'posted',
                relatedShipmentId: shipmentId,
                relatedEmployeeId: employee.id,
                relatedExpenseId: expenseId,
                createdBy,
                createdAt: new Date(),
                updatedAt: new Date()
            };

            await journalEntriesService.addJournalEntry(journalEntryData);

            console.log('✅ Commission expense and journal entry created successfully');
        } catch (error) {
            console.error('❌ Error creating commission expense and journal entry:', error);
            throw error;
        }
    },

    /**
     * Process payroll accrual with automation settings from company settings
     */
    async processPayrollAccrual(companyId, periodId, createdBy) {
        try {
            console.log('🔄 Processing payroll accrual:', { companyId, periodId, createdBy });

            // Get the period
            const periodRef = doc(db, 'payrollPeriods', periodId);
            const periodDoc = await getDoc(periodRef);
            
            if (!periodDoc.exists()) {
                throw new Error('Payroll period not found');
            }

            const period = { id: periodDoc.id, ...periodDoc.data() };
            
            if (period.status !== 'draft') {
                throw new Error('Can only process draft periods');
            }

            // Load automation settings from company settings
            let automationSettings = {
                createJournalEntry: false,
                createExpense: false,
                salaryAccount: '5100',
                commissionAccount: '5200',
                cashAccount: '1000',
                expenseCategory: 'payroll'
            };

            try {
                const { companySettingsService } = await import('../system/company-settings');
                const companySettings = await companySettingsService.getCompanySettings(companyId);
                
                if (companySettings?.payrollSettings) {
                    automationSettings = {
                        createJournalEntry: companySettings.payrollSettings.defaultCreateJournalEntry ?? false,
                        createExpense: companySettings.payrollSettings.defaultCreateExpense ?? false,
                        salaryAccount: companySettings.payrollSettings.defaultSalaryAccount || '5100',
                        commissionAccount: companySettings.payrollSettings.defaultCommissionAccount || '5200',
                        cashAccount: companySettings.payrollSettings.defaultCashAccount || '1000',
                        expenseCategory: companySettings.payrollSettings.defaultExpenseCategory || 'payroll'
                    };
                    console.log('✅ Loaded automation settings from company settings:', automationSettings);
                }
            } catch (settingsError) {
                console.warn('⚠️ Could not load company settings, using defaults:', settingsError);
            }

            // Get employees
            const employees = await this.getEmployees(companyId);
            const activeEmployees = employees.filter(emp => emp.status === 'active');
            const startDate = new Date(period.startDate);
            const endDate = new Date(period.endDate);

            const batch = writeBatch(db);
            const payrollEntriesRef = collection(db, 'payrollEntries');
            
            let totalSalaries = 0;
            let totalCommissions = 0;
            const processedEmployees = [];

            // Process each active employee
            for (const employee of activeEmployees) {
                const salary = parseFloat(employee.salary) || 0;
                let commission = 0;
                let commissionDetails = [];
                
                // Calculate commissions for drivers
                if (employee.type === 'driver' && employee.commissionEligible) {
                    try {
                        const commissionData = await this.calculateDriverCommissions(
                            employee.id,
                            companyId,
                            startDate,
                            endDate
                        );
                        commission = commissionData.unpaidCommissions || 0;
                        commissionDetails = commissionData.commissions || [];
                    } catch (commissionError) {
                        console.warn('⚠️ Failed to calculate commission for employee:', employee.name, commissionError);
                    }
                }
                
                const totalDue = salary + commission;
                
                if (totalDue > 0) {
                    // Create payroll entry
                    const payrollEntryRef = doc(payrollEntriesRef);
                    const payrollEntry = {
                        id: payrollEntryRef.id,
                        companyId,
                        periodId,
                        employeeId: employee.id,
                        employeeName: employee.name,
                        employeeRole: employee.role || 'Employee',
                        employeeType: employee.type || 'employee',
                        employeeCode: employee.employeeId || employee.driverCode || `EMP${employee.id.slice(-4)}`,
                        salary,
                        commission,
                        totalDue,
                        paymentStatus: 'accrued',
                        commissionDetails,
                        createdBy,
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp()
                    };
                    
                    batch.set(payrollEntryRef, payrollEntry);
                    
                    totalSalaries += salary;
                    totalCommissions += commission;
                    processedEmployees.push(payrollEntry);
                }
            }

            // Update period status and totals
            batch.update(periodRef, {
                status: 'accrued',
                totalSalaries,
                totalCommissions,
                totalPayable: totalSalaries + totalCommissions,
                employeeCount: processedEmployees.length,
                processedBy: createdBy,
                processedAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            // Commit the batch
            await batch.commit();
            console.log('✅ Payroll entries created successfully');

            // Generate payslips for all employees
            try {
                console.log('🔄 Generating payslips for all employees...');
                const { payslipsService } = await import('./payslips');
                await payslipsService.generatePeriodPayslips(companyId, periodId, createdBy);
                console.log('✅ Payslips generated successfully');
            } catch (payslipError) {
                console.warn('⚠️ Failed to generate payslips:', payslipError);
                // Don't fail the whole process if payslip generation fails
            }

            // Handle automation based on company settings
            if (automationSettings.createJournalEntry || automationSettings.createExpense) {
                console.log('🔄 Creating automated entries based on company settings...');
                await this.createAutomatedEntries(
                    companyId,
                    period,
                    processedEmployees,
                    automationSettings,
                    createdBy
                );
            }

            return {
                success: true,
                totalSalaries,
                totalCommissions,
                totalPayable: totalSalaries + totalCommissions,
                employeeCount: processedEmployees.length,
                automationApplied: {
                    journalEntry: automationSettings.createJournalEntry,
                    expense: automationSettings.createExpense
                }
            };

        } catch (error) {
            console.error('❌ Error processing payroll accrual:', error);
            throw error;
        }
    },

    /**
     * Create automated journal entries and expenses based on settings
     */
    async createAutomatedEntries(companyId, period, payrollEntries, automationSettings, createdBy) {
        try {
            console.log('🔄 Creating automated entries:', automationSettings);

            const totalSalaries = payrollEntries.reduce((sum, entry) => sum + (entry.salary || 0), 0);
            const totalCommissions = payrollEntries.reduce((sum, entry) => sum + (entry.commission || 0), 0);
            const totalPayable = totalSalaries + totalCommissions;

            // Create journal entry if requested
            if (automationSettings.createJournalEntry && totalPayable > 0) {
                await this.createPayrollJournalEntry(
                    companyId,
                    period,
                    totalSalaries,
                    totalCommissions,
                    automationSettings,
                    createdBy
                );
            }

            // Create expenses if requested
            if (automationSettings.createExpense && totalPayable > 0) {
                await this.createPayrollExpenses(
                    companyId,
                    period,
                    payrollEntries,
                    automationSettings,
                    createdBy
                );
            }

        } catch (error) {
            console.error('❌ Error creating automated entries:', error);
            // Don't fail the whole payroll process if automation fails
            console.warn('⚠️ Continuing without automated entries');
        }
    },

    /**
     * Create payroll journal entry
     */
    async createPayrollJournalEntry(companyId, period, totalSalaries, totalCommissions, automationSettings, createdBy) {
        try {
            console.log('🔄 Creating payroll journal entry:', { companyId, period: period.id, totalSalaries, totalCommissions });
            
            const { journalEntriesService } = await import('./journal-entries');
            const { chartOfAccountsService } = await import('./chart-of-accounts');

            const lineItems = [];
            let totalDebit = 0;
            let totalCredit = 0;
            
            // Get chart of accounts to validate account codes
            const accounts = await chartOfAccountsService.getAccounts(companyId);
            console.log('📊 Available accounts:', accounts.length);
            
            // Helper function to find account by code
            const findAccountByCode = (code) => {
                const account = accounts.find(acc => acc.code === code || acc.accountNumber === code);
                if (!account) {
                    console.error(`❌ Account not found for code: ${code}`);
                    console.log('Available account codes:', accounts.map(acc => ({ code: acc.code, name: acc.name })));
                }
                return account;
            };
            
            // Salary expense entry
            if (totalSalaries > 0) {
                const salaryAccount = findAccountByCode(automationSettings.salaryAccount || '5100');
                if (salaryAccount) {
                    lineItems.push({
                        accountId: salaryAccount.id,
                        accountCode: salaryAccount.code || salaryAccount.accountNumber,
                        accountName: salaryAccount.name,
                        debit: totalSalaries,
                        credit: 0,
                        description: `Salary expenses for ${period.year}-${String(period.month).padStart(2, '0')}`
                    });
                    totalDebit += totalSalaries;
                    console.log('✅ Added salary expense entry:', totalSalaries);
                } else {
                    // Create a default salary expense account if not found
                    console.warn('⚠️ Salary account not found, using default');
                    lineItems.push({
                        accountId: null,
                        accountCode: automationSettings.salaryAccount || '5100',
                        accountName: 'Salary Expenses',
                        debit: totalSalaries,
                        credit: 0,
                        description: `Salary expenses for ${period.year}-${String(period.month).padStart(2, '0')}`
                    });
                    totalDebit += totalSalaries;
                }
            }

            // Commission expense entry
            if (totalCommissions > 0) {
                const commissionAccount = findAccountByCode(automationSettings.commissionAccount || '5200');
                if (commissionAccount) {
                    lineItems.push({
                        accountId: commissionAccount.id,
                        accountCode: commissionAccount.code || commissionAccount.accountNumber,
                        accountName: commissionAccount.name,
                        debit: totalCommissions,
                        credit: 0,
                        description: `Commission expenses for ${period.year}-${String(period.month).padStart(2, '0')}`
                    });
                    totalDebit += totalCommissions;
                    console.log('✅ Added commission expense entry:', totalCommissions);
                } else {
                    // Create a default commission expense account if not found
                    console.warn('⚠️ Commission account not found, using default');
                    lineItems.push({
                        accountId: null,
                        accountCode: automationSettings.commissionAccount || '5200',
                        accountName: 'Commission Expenses',
                        debit: totalCommissions,
                        credit: 0,
                        description: `Commission expenses for ${period.year}-${String(period.month).padStart(2, '0')}`
                    });
                    totalDebit += totalCommissions;
                }
            }

            // Cash credit entry (or Accrued Payroll Liability)
            const totalPayable = totalSalaries + totalCommissions;
            const cashAccount = findAccountByCode(automationSettings.cashAccount || '2200'); // Use Accrued Payroll instead of Cash
            if (cashAccount) {
                lineItems.push({
                    accountId: cashAccount.id,
                    accountCode: cashAccount.code || cashAccount.accountNumber,
                    accountName: cashAccount.name,
                    debit: 0,
                    credit: totalPayable,
                    description: `Payroll accrual for ${period.year}-${String(period.month).padStart(2, '0')}`
                });
                totalCredit += totalPayable;
                console.log('✅ Added cash/liability credit entry:', totalPayable);
            } else {
                // Create a default accrued payroll account if not found
                console.warn('⚠️ Cash/Liability account not found, using default');
                lineItems.push({
                    accountId: null,
                    accountCode: automationSettings.cashAccount || '2200',
                    accountName: 'Accrued Payroll',
                    debit: 0,
                    credit: totalPayable,
                    description: `Payroll accrual for ${period.year}-${String(period.month).padStart(2, '0')}`
                });
                totalCredit += totalPayable;
            }

            // Validate that debits equal credits
            if (Math.abs(totalDebit - totalCredit) > 0.01) {
                throw new Error(`Journal entry is not balanced: Debit ${totalDebit} != Credit ${totalCredit}`);
            }

            const journalEntryData = {
                companyId,
                date: new Date().toISOString().split('T')[0],
                reference: `PAYROLL-${period.year}${String(period.month).padStart(2, '0')}`,
                description: `Payroll accrual for ${period.year}-${String(period.month).padStart(2, '0')}`,
                entryType: 'payroll_accrual',
                sourceType: 'payroll_period',
                sourceId: period.id,
                lineItems,
                totalDebit,
                totalCredit,
                status: 'posted', // Post immediately
                relatedPeriodId: period.id,
                createdBy,
                createdAt: new Date(),
                updatedAt: new Date()
            };

            console.log('📝 Creating journal entry with data:', journalEntryData);
            await journalEntriesService.addJournalEntry(journalEntryData);
            console.log('✅ Payroll journal entry created successfully');

        } catch (error) {
            console.error('❌ Error creating payroll journal entry:', error);
            throw error;
        }
    },

    /**
     * Create payroll expenses
     */
    async createPayrollExpenses(companyId, period, payrollEntries, automationSettings, createdBy) {
        try {
            const { expensesService } = await import('./expenses');

            // Group by employee type or create individual expenses
            for (const entry of payrollEntries) {
                if (entry.totalDue > 0) {
                    const expenseData = {
                        companyId,
                        category: automationSettings.expenseCategory || 'payroll',
                        amount: entry.totalDue,
                        description: `Payroll - ${entry.employeeName} (${period.year}-${String(period.month).padStart(2, '0')})`,
                        date: new Date().toISOString().split('T')[0],
                        vendor: entry.employeeName,
                        paymentMethod: 'cash',
                        vatRate: 0,
                        vatAmount: 0,
                        totalAmount: entry.totalDue,
                        status: 'approved',
                        tags: ['payroll', 'automated'],
                        attachments: [],
                        receiptAttached: false,
                        notes: `Salary: ${entry.salary}, Commission: ${entry.commission}`,
                        relatedPeriodId: period.id,
                        relatedEmployeeId: entry.employeeId,
                        createdBy: 'system',
                        createdAt: new Date(),
                        updatedAt: new Date()
                    };

                    await expensesService.addExpense(expenseData);
                }
            }

            console.log('✅ Payroll expenses created');

        } catch (error) {
            console.error('❌ Error creating payroll expenses:', error);
            throw error;
        }
    },

    /**
     * Reverse commission expense and journal entry
     */
    async reverseCommissionExpenseAndJournal(commissionDetails, createdBy) {
        try {
            const { shipment, employee, commissionAmount, shipmentId } = commissionDetails;

            if (commissionAmount <= 0) return;

            // Import services dynamically to avoid circular dependencies
            const { expensesService } = await import('./expenses');
            const { journalEntriesService } = await import('./journal-entries');

            // Find and delete the related expense
            const expenses = await expensesService.getExpenses(shipment.companyId);
            const relatedExpense = expenses.find(exp =>
                exp.relatedShipmentId === shipmentId &&
                exp.relatedEmployeeId === employee.id &&
                exp.category === 'commission'
            );

            if (relatedExpense) {
                await expensesService.deleteExpense(relatedExpense.id);
                console.log('✅ Commission expense deleted');
            }

            // Create reversing journal entry
            const reversingJournalEntryData = {
                companyId: shipment.companyId,
                date: new Date().toISOString().split('T')[0],
                reference: `REV-COMM-${shipmentId.slice(-8)}`,
                description: `Reverse commission payment - ${employee.name} - ${shipment.trackingNumber || shipmentId.slice(-8)}`,
                type: 'commission_reversal',
                entries: [
                    {
                        accountCode: '1000', // Cash
                        accountName: 'Cash',
                        debit: commissionAmount,
                        credit: 0,
                        description: `Reverse commission payment to ${employee.name}`
                    },
                    {
                        accountCode: '5700', // Commission Expense
                        accountName: 'Commission Expenses',
                        debit: 0,
                        credit: commissionAmount,
                        description: `Reverse commission expense for ${employee.name}`
                    }
                ],
                totalDebit: commissionAmount,
                totalCredit: commissionAmount,
                status: 'posted',
                relatedShipmentId: shipmentId,
                relatedEmployeeId: employee.id,
                createdBy,
                createdAt: new Date(),
                updatedAt: new Date()
            };

            await journalEntriesService.addJournalEntry(reversingJournalEntryData);

            console.log('✅ Commission reversal journal entry created successfully');
        } catch (error) {
            console.error('❌ Error reversing commission expense and journal entry:', error);
            throw error;
        }
    },

    /**
     * Get payroll periods
     */
    async getPayrollPeriods(companyId) {
        try {
            if (!companyId) {
                console.warn('No company ID provided to getPayrollPeriods');
                return [];
            }

            const periodsRef = collection(db, 'payrollPeriods');

            try {
                const simpleQuery = query(periodsRef, where('companyId', '==', companyId));
                const snapshot = await getDocs(simpleQuery);

                const periods = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                return periods.sort((a, b) => {
                    if (a.year !== b.year) return b.year - a.year;
                    return b.month - a.month;
                });
            } catch (queryError) {
                console.warn('Error querying payroll periods:', queryError);
                return [];
            }
        } catch (error) {
            console.error('Error getting payroll periods:', error);
            return [];
        }
    },

    /**
     * Process payroll accrual for a period
     */
    async processPayrollAccrual(companyId, periodId, createdBy) {
        try {
            console.log('🔄 Processing payroll accrual:', { companyId, periodId, createdBy });
            
            // Get the payroll period
            const periodRef = doc(db, 'payrollPeriods', periodId);
            const periodSnapshot = await getDoc(periodRef);
            
            if (!periodSnapshot.exists()) {
                throw new Error('Payroll period not found');
            }
            
            const period = { id: periodSnapshot.id, ...periodSnapshot.data() };
            console.log('📅 Processing period:', period);
            
            if (period.status !== 'draft') {
                throw new Error('Payroll period is not in draft status');
            }
            
            const startDate = new Date(period.startDate);
            const endDate = new Date(period.endDate);
            
            // Get all employees
            const employees = await this.getEmployees(companyId);
            console.log('👥 Processing employees:', employees.length);
            
            const batch = writeBatch(db);
            const payrollEntriesRef = collection(db, 'payrollEntries');
            
            let totalSalaries = 0;
            let totalCommissions = 0;
            let totalBonuses = 0;
            let totalDeductions = 0;
            let totalPayable = 0;
            
            for (const employee of employees) {
                console.log('👤 Processing employee:', employee.name);
                
                const salary = employee.salary || 0;
                let commission = 0;
                let commissionDetails = [];
                
                // Calculate commissions for commission-eligible employees
                if (employee.commissionEligible && employee.type === 'driver') {
                    try {
                        const commissionData = await this.calculateDriverCommissions(
                            employee.id,
                            companyId,
                            startDate,
                            endDate
                        );
                        commission = commissionData.unpaidCommissions || 0; // Only unpaid commissions for payroll
                        commissionDetails = commissionData.commissions || [];
                        console.log('💰 Commission calculated:', commission);
                    } catch (commissionError) {
                        console.warn('⚠️ Failed to calculate commission for employee:', employee.name, commissionError);
                    }
                }
                
                const bonus = 0; // TODO: Implement bonus calculation
                const deductions = 0; // TODO: Implement deductions calculation
                const totalDue = salary + commission + bonus - deductions;
                
                // Create payroll entry
                const payrollEntry = {
                    companyId,
                    periodId,
                    employeeId: employee.id,
                    employeeName: employee.name,
                    employeeRole: employee.role,
                    employeeType: employee.type,
                    salary,
                    commission,
                    bonus,
                    deductions,
                    totalDue,
                    paymentStatus: 'accrued',
                    commissionDetails,
                    createdBy,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                };
                
                // Add to batch
                const entryRef = doc(payrollEntriesRef);
                batch.set(entryRef, payrollEntry);
                
                // Update totals
                totalSalaries += salary;
                totalCommissions += commission;
                totalBonuses += bonus;
                totalDeductions += deductions;
                totalPayable += totalDue;
                
                console.log('✅ Payroll entry prepared for:', employee.name, 'Total:', totalDue);
            }
            
            // Update period status and totals
            batch.update(periodRef, {
                status: 'accrued',
                totalSalaries,
                totalCommissions,
                totalBonuses,
                totalDeductions,
                totalPayable,
                employeeCount: employees.length,
                processedBy: createdBy,
                processedAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
            
            // Create journal entry for payroll accrual
            try {
                const journalEntry = {
                    companyId,
                    date: endDate.toISOString().split('T')[0],
                    reference: `PAYROLL-${periodId}`,
                    description: `Payroll accrual for ${period.year}-${String(period.month).padStart(2, '0')}`,
                    type: 'payroll_accrual',
                    entries: [
                        {
                            accountCode: '5100',
                            accountName: 'Salaries and Wages',
                            debit: totalSalaries,
                            credit: 0,
                            description: 'Salary expense accrual'
                        },
                        {
                            accountCode: '5700',
                            accountName: 'Commission Expenses',
                            debit: totalCommissions,
                            credit: 0,
                            description: 'Commission expense accrual'
                        },
                        {
                            accountCode: '2200',
                            accountName: 'Accrued Payroll',
                            debit: 0,
                            credit: totalPayable,
                            description: 'Payroll liability accrual'
                        }
                    ],
                    totalDebit: totalSalaries + totalCommissions,
                    totalCredit: totalPayable,
                    status: 'posted',
                    relatedPeriodId: periodId,
                    createdBy,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                };
                
                const journalRef = doc(collection(db, 'journalEntries'));
                batch.set(journalRef, journalEntry);
                console.log('📊 Journal entry prepared');
            } catch (journalError) {
                console.warn('⚠️ Failed to create journal entry:', journalError);
                // Continue without journal entry - don't fail the whole process
            }
            
            // Commit all changes
            await batch.commit();
            console.log('✅ Payroll accrual processed successfully');
            
            return {
                success: true,
                totalSalaries,
                totalCommissions,
                totalPayable,
                employeeCount: employees.length
            };
            
        } catch (error) {
            console.error('❌ Error processing payroll accrual:', error);
            throw error;
        }
    },

    /**
     * Create payroll period
     */
    async createPayrollPeriod(companyId, year, month, createdBy) {
        try {
            console.log('🔄 Creating payroll period:', { companyId, year, month, createdBy });
            
            const periodId = this.generatePayrollPeriodId(year, month);
            const startDate = new Date(year, month - 1, 1);
            const endDate = new Date(year, month, 0);

            // Check if period already exists
            const existingPeriodRef = doc(db, 'payrollPeriods', periodId);
            const existingPeriodSnapshot = await getDoc(existingPeriodRef);
            
            if (existingPeriodSnapshot.exists()) {
                throw new Error(`Payroll period for ${year}-${String(month).padStart(2, '0')} already exists`);
            }

            const periodData = {
                companyId,
                year: parseInt(year),
                month: parseInt(month),
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                status: 'draft',
                totalSalaries: 0,
                totalCommissions: 0,
                totalBonuses: 0,
                totalDeductions: 0,
                totalPayable: 0,
                employeeCount: 0,
                createdBy,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            // Use the generated periodId as the document ID
            await setDoc(existingPeriodRef, periodData);
            
            console.log('✅ Payroll period created successfully with ID:', periodId);
            return periodId;
        } catch (error) {
            console.error('❌ Error creating payroll period:', error);
            throw error;
        }
    },

    /**
     * Update payroll period status
     */
    async updatePayrollPeriodStatus(periodId, status, updatedBy) {
        try {
            console.log('🔄 Updating payroll period status:', { periodId, status, updatedBy });
            
            // First check if the document exists
            const periodRef = doc(db, 'payrollPeriods', periodId);
            const periodSnapshot = await getDoc(periodRef);
            
            if (!periodSnapshot.exists()) {
                throw new Error(`Payroll period with ID ${periodId} not found`);
            }
            
            await updateDoc(periodRef, {
                status,
                updatedBy,
                updatedAt: serverTimestamp()
            });
            
            console.log('✅ Payroll period status updated successfully');
            return true;
        } catch (error) {
            console.error('❌ Error updating payroll period status:', error);
            throw error;
        }
    },

    /**
     * Delete payroll period
     */
    async deletePayrollPeriod(periodId) {
        try {
            console.log('🗑️ Deleting payroll period:', periodId);
            
            // Check if the specific document exists
            const periodRef = doc(db, 'payrollPeriods', periodId);
            const periodSnapshot = await getDoc(periodRef);
            
            if (!periodSnapshot.exists()) {
                console.log('❌ Period not found with ID:', periodId);
                
                // Try to find periods with similar data (for old format IDs like "2025-07")
                const periodsRef = collection(db, 'payrollPeriods');
                const allPeriodsSnapshot = await getDocs(periodsRef);
                console.log('📋 Available periods in database:');
                allPeriodsSnapshot.docs.forEach(doc => {
                    console.log(`  - ID: ${doc.id}, Data:`, doc.data());
                });
                
                // Check if this is an old format ID (like "2025-07")
                if (periodId.includes('-') && periodId.length === 7) {
                    console.log('🔍 This looks like an old format ID, trying to find matching periods...');
                    const matchingPeriods = allPeriodsSnapshot.docs.filter(doc => {
                        const data = doc.data();
                        const periodKey = `${data.year}-${String(data.month).padStart(2, '0')}`;
                        return periodKey === periodId;
                    });
                    
                    if (matchingPeriods.length > 0) {
                        console.log(`✅ Found ${matchingPeriods.length} periods matching ${periodId}`);
                        // Delete all matching periods
                        const batch = writeBatch(db);
                        matchingPeriods.forEach(doc => {
                            batch.delete(doc.ref);
                        });
                        await batch.commit();
                        console.log(`✅ Deleted ${matchingPeriods.length} periods with old format ID`);
                        return true;
                    }
                }
                
                throw new Error(`Payroll period with ID ${periodId} not found`);
            }
            
            const periodData = periodSnapshot.data();
            console.log('📋 Period data:', periodData);
            
            // Check if period has entries
            const entriesRef = collection(db, 'payrollEntries');
            const q = query(entriesRef, where('periodId', '==', periodId));
            const entriesSnapshot = await getDocs(q);
            
            console.log('📊 Found payroll entries:', entriesSnapshot.size);
            
            if (!entriesSnapshot.empty) {
                console.log('❌ Cannot delete period with existing entries');
                throw new Error('Cannot delete period with existing payroll entries');
            }
            
            // Delete the period
            await deleteDoc(periodRef);
            
            console.log('✅ Payroll period deleted successfully');
            return true;
        } catch (error) {
            console.error('❌ Error deleting payroll period:', error);
            console.error('Error details:', error.message);
            throw error;
        }
    },

    /**
     * Clean up duplicate payroll periods
     */
    async cleanupDuplicatePeriods(companyId) {
        try {
            console.log('🧹 Cleaning up duplicate payroll periods for company:', companyId);
            
            const periodsRef = collection(db, 'payrollPeriods');
            const q = query(periodsRef, where('companyId', '==', companyId));
            const snapshot = await getDocs(q);
            
            const periods = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            console.log('📋 Found periods:', periods.length);
            
            // Group periods by year and month
            const periodGroups = {};
            periods.forEach(period => {
                const key = `${period.year}-${String(period.month).padStart(2, '0')}`;
                if (!periodGroups[key]) {
                    periodGroups[key] = [];
                }
                periodGroups[key].push(period);
            });
            
            // Find duplicates
            const duplicates = [];
            Object.entries(periodGroups).forEach(([key, groupPeriods]) => {
                if (groupPeriods.length > 1) {
                    console.log(`🔍 Found ${groupPeriods.length} duplicates for period ${key}:`);
                    groupPeriods.forEach(period => {
                        console.log(`  - ID: ${period.id}, Created: ${period.createdAt}, Status: ${period.status}`);
                    });
                    duplicates.push(...groupPeriods);
                }
            });
            
            if (duplicates.length === 0) {
                console.log('✅ No duplicate periods found');
                return { deleted: 0, message: 'No duplicate periods found' };
            }
            
            // Delete duplicates (keep the first one created)
            const batch = writeBatch(db);
            let deletedCount = 0;
            
            Object.entries(periodGroups).forEach(([key, groupPeriods]) => {
                if (groupPeriods.length > 1) {
                    // Sort by creation date and keep the oldest
                    const sortedPeriods = groupPeriods.sort((a, b) => {
                        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
                        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
                        return dateA - dateB;
                    });
                    
                    // Keep the first (oldest) one, delete the rest
                    const toDelete = sortedPeriods.slice(1);
                    console.log(`🗑️ Keeping period ${sortedPeriods[0].id}, deleting ${toDelete.length} duplicates`);
                    
                    toDelete.forEach(period => {
                        const periodRef = doc(db, 'payrollPeriods', period.id);
                        batch.delete(periodRef);
                        deletedCount++;
                    });
                }
            });
            
            if (deletedCount > 0) {
                await batch.commit();
                console.log(`✅ Deleted ${deletedCount} duplicate periods`);
            }
            
            return { deleted: deletedCount, message: `Deleted ${deletedCount} duplicate periods` };
        } catch (error) {
            console.error('❌ Error cleaning up duplicate periods:', error);
            throw error;
        }
    },

    /**
     * Clean up old format payroll periods (IDs like "2025-07")
     */
    async cleanupOldFormatPeriods(companyId) {
        try {
            console.log('🧹 Cleaning up old format payroll periods for company:', companyId);
            
            const periodsRef = collection(db, 'payrollPeriods');
            const q = query(periodsRef, where('companyId', '==', companyId));
            const snapshot = await getDocs(q);
            
            const oldFormatPeriods = [];
            const newFormatPeriods = [];
            
            // Separate old and new format periods
            snapshot.docs.forEach(doc => {
                const period = { id: doc.id, ...doc.data() };
                // Check if ID is old format (like "2025-07")
                if (doc.id.includes('-') && doc.id.length === 7 && /^\d{4}-\d{2}$/.test(doc.id)) {
                    oldFormatPeriods.push(period);
                } else {
                    newFormatPeriods.push(period);
                }
            });
            
            console.log(`📋 Found ${oldFormatPeriods.length} old format periods and ${newFormatPeriods.length} new format periods`);
            
            if (oldFormatPeriods.length === 0) {
                console.log('✅ No old format periods found');
                return { deleted: 0, message: 'No old format periods found' };
            }
            
            // Group old format periods by year-month
            const periodGroups = {};
            oldFormatPeriods.forEach(period => {
                const key = period.id; // This is already in "2025-07" format
                if (!periodGroups[key]) {
                    periodGroups[key] = [];
                }
                periodGroups[key].push(period);
            });
            
            // Delete all old format periods
            const batch = writeBatch(db);
            let deletedCount = 0;
            
            Object.entries(periodGroups).forEach(([key, periods]) => {
                console.log(`🗑️ Deleting ${periods.length} old format periods for ${key}`);
                periods.forEach(period => {
                    const periodRef = doc(db, 'payrollPeriods', period.id);
                    batch.delete(periodRef);
                    deletedCount++;
                });
            });
            
            if (deletedCount > 0) {
                await batch.commit();
                console.log(`✅ Deleted ${deletedCount} old format periods`);
            }
            
            return { deleted: deletedCount, message: `Deleted ${deletedCount} old format periods` };
        } catch (error) {
            console.error('❌ Error cleaning up old format periods:', error);
            throw error;
        }
    },

    /**
     * Get payroll period by ID
     */
    async getPayrollPeriod(periodId) {
        try {
            const periodRef = doc(db, 'payrollPeriods', periodId);
            const snapshot = await getDoc(periodRef);
            
            if (snapshot.exists()) {
                return { id: snapshot.id, ...snapshot.data() };
            }
            return null;
        } catch (error) {
            console.error('Error getting payroll period:', error);
            throw error;
        }
    },

    /**
     * Get payroll entries for a specific period
     */
    async getPayrollEntries(companyId, periodId) {
        try {
            if (!companyId || !periodId) {
                console.warn('Missing companyId or periodId for getPayrollEntries');
                return [];
            }

            const entriesRef = collection(db, 'payrollEntries');
            const q = query(
                entriesRef,
                where('companyId', '==', companyId),
                where('periodId', '==', periodId)
            );

            const snapshot = await getDocs(q);
            const entries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            console.log('📋 Payroll entries loaded:', entries.length);
            return entries.sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));
        } catch (error) {
            console.error('Error getting payroll entries:', error);
            throw error;
        }
    },

    /**
     * Process payroll for a period
     */
    async processPayroll(companyId, periodId, processedBy) {
        try {
            console.log('🔄 Processing payroll for period:', periodId);

            // Get the period details
            const periodsRef = collection(db, 'payrollPeriods');
            const periodQuery = query(periodsRef, where('companyId', '==', companyId), where('id', '==', periodId));
            const periodSnapshot = await getDocs(periodQuery);

            if (periodSnapshot.empty) {
                throw new Error('Payroll period not found');
            }

            const period = { id: periodSnapshot.docs[0].id, ...periodSnapshot.docs[0].data() };
            const startDate = new Date(period.startDate);
            const endDate = new Date(period.endDate);

            // Get all employees
            const employees = await this.getEmployees(companyId);
            console.log('👥 Processing payroll for', employees.length, 'employees');

            const batch = writeBatch(db);
            const entriesRef = collection(db, 'payrollEntries');

            let totalSalaries = 0;
            let totalCommissions = 0;
            let totalBonuses = 0;
            let totalPayable = 0;

            // Process each employee
            for (const employee of employees) {
                console.log('👤 Processing employee:', employee.name);

                // Calculate base salary
                const salary = employee.salary || 0;

                // Calculate commissions if eligible
                let commission = 0;
                if (employee.commissionEligible) {
                    try {
                        const commissions = await this.getEmployeeCommissions(
                            employee.id,
                            companyId,
                            startDate,
                            endDate
                        );
                        commission = commissions.reduce((sum, comm) => sum + (comm.amount || 0), 0);
                        console.log('💰 Commission calculated:', commission, 'for', employee.name);
                    } catch (commissionError) {
                        console.warn('⚠️ Error calculating commission for', employee.name, commissionError);
                    }
                }

                // Calculate total due
                const totalDue = salary + commission;

                // Create payroll entry
                const entryData = {
                    companyId,
                    periodId,
                    employeeId: employee.id,
                    employeeName: employee.name,
                    employeeRole: employee.role,
                    employeeType: employee.type,
                    salary,
                    commission,
                    bonus: 0, // Can be added later via adjustments
                    deductions: 0, // Can be added later via adjustments
                    totalDue,
                    paymentStatus: 'accrued',
                    paymentDate: null,
                    paymentMethod: null,
                    paymentReference: null,
                    notes: '',
                    createdBy: processedBy,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                };

                // Add to batch
                const entryRef = doc(entriesRef);
                batch.set(entryRef, entryData);

                // Update totals
                totalSalaries += salary;
                totalCommissions += commission;
                totalPayable += totalDue;
            }

            // Update period status and totals
            const periodRef = doc(db, 'payrollPeriods', periodSnapshot.docs[0].id);
            batch.update(periodRef, {
                status: 'accrued',
                totalSalaries,
                totalCommissions,
                totalBonuses,
                totalPayable,
                employeeCount: employees.length,
                processedBy,
                processedAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            // Commit all changes
            await batch.commit();

            console.log('✅ Payroll processed successfully');
            return {
                totalSalaries,
                totalCommissions,
                totalBonuses,
                totalPayable,
                employeeCount: employees.length
            };
        } catch (error) {
            console.error('❌ Error processing payroll:', error);
            throw error;
        }
    }
};